import React, { useEffect, useState } from "react";
import { api } from "../context/AuthContext";
import { useI18n } from "../context/I18nContext";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { dateTimeFmt, dateFmt } from "../lib/format";
import { ShieldCheck, FileXls, CaretDown, CaretRight } from "@phosphor-icons/react";

export const VerificationsPanel = () => {
  const { t, lang } = useI18n();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [openCode, setOpenCode] = useState(null);
  const [events, setEvents] = useState({});

  useEffect(() => {
    api.get("/admin/verification-logs").then((r) => setRows(r.data)).catch(() => {});
  }, []);

  const filtered = rows.filter((r) => (r.code || "").toLowerCase().includes(q.toLowerCase()));

  const toggle = async (code) => {
    if (openCode === code) { setOpenCode(null); return; }
    setOpenCode(code);
    if (!events[code]) {
      try {
        const { data } = await api.get(`/admin/verification-logs/${encodeURIComponent(code)}/events`);
        setEvents((e) => ({ ...e, [code]: data }));
      } catch { setEvents((e) => ({ ...e, [code]: [] })); }
    }
  };

  const exportCsv = () => {
    const head = "Folio,Verificaciones,Ultima\n";
    const body = filtered.map((r) => `${r.code},${r.count},${(r.last_at || "").slice(0, 10)}`).join("\n");
    const blob = new Blob([head + body], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "verificaciones.csv";
    a.click();
  };

  return (
    <div className="space-y-5" data-testid="verifications-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-2xl font-display font-bold text-emerald-900 flex items-center gap-2">
          <ShieldCheck size={24} weight="fill" className="text-emerald-700" /> {t("verif_tab")}
        </h2>
        <Button data-testid="verif-export-btn" variant="outline" onClick={exportCsv} disabled={filtered.length === 0}>
          <FileXls size={16} className="mr-1" /> CSV
        </Button>
      </div>
      <Input data-testid="verif-search" placeholder="Buscar folio..." value={q} onChange={(e) => setQ(e.target.value)} className="max-w-sm" />
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm divide-y">
        {filtered.length === 0 ? (
          <p className="text-center text-slate-400 py-8">—</p>
        ) : filtered.map((r) => (
          <div key={r.code} data-testid={`verif-row-${r.code}`}>
            <button
              type="button"
              onClick={() => toggle(r.code)}
              data-testid={`verif-toggle-${r.code}`}
              className="w-full flex items-center justify-between px-4 py-3 text-sm hover:bg-slate-50 transition-colors text-left"
            >
              <span className="flex items-center gap-2 font-mono-num font-semibold text-emerald-800">
                {openCode === r.code ? <CaretDown size={14} weight="bold" /> : <CaretRight size={14} weight="bold" />}
                {r.code}
              </span>
              <span className="text-slate-500">{r.count} {t("verif_times")} · {dateFmt(r.last_at, lang)}</span>
            </button>
            {openCode === r.code && (
              <div className="bg-slate-50 px-6 py-3 border-t" data-testid={`verif-events-${r.code}`}>
                {!events[r.code] ? (
                  <p className="text-xs text-slate-400">…</p>
                ) : events[r.code].length === 0 ? (
                  <p className="text-xs text-slate-400">—</p>
                ) : (
                  <ul className="space-y-1.5">
                    {events[r.code].map((ev, i) => (
                      <li key={i} className="flex items-center justify-between text-xs">
                        <span className="font-mono-num text-slate-600">{dateTimeFmt(ev.at, lang)}</span>
                        <span className={ev.valid ? "text-emerald-600 font-semibold" : "text-red-500 font-semibold"}>
                          {ev.valid ? "✓ Válido" : "✕ No encontrado"}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
